import { Download, FileText } from "lucide-react";
import type { QuoteRequestPage } from "@shared-core/data/supplier-portal-adapter";
import type { QuoteRequestFile } from "@shared-core/data/supplier-api-types";
import {
  QUOTE_REQUEST_WORDING,
  fileSize,
  poundsFromPence,
  quantityLabel,
  weekdayDayMonth,
} from "@shared-core/domain/supplier-quote-request";
import { ACTION_WORDING } from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { cn } from "../../app/components/primitives/utils";
import { StaffViewReadOnly } from "../../app/components/domain/StaffViewReadOnly";

/** The prices as the supplier sent them: each quantity's price or Can't quote, the dates, the reference and the PDF. */
export function SentPrices({
  page,
  onChange,
  onDownload,
}: {
  page: QuoteRequestPage;
  /** *Change our prices*; absent once the request is closed. */
  onChange: (() => void) | null;
  onDownload: (file: QuoteRequestFile) => void;
}) {
  const quotePdf = page.your_quote_document;
  return (
    <Card aria-label="Your prices" role="group" className="min-w-0 gap-3 p-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-36">
              {QUOTE_REQUEST_WORDING.quantity}
            </TableHead>
            <TableHead>Your price</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {page.quantities.map((row) => (
            <TableRow
              key={row.quantity}
              className={cn(row.not_quoted && "text-muted-foreground")}
            >
              <TableCell className="font-semibold tabular-nums">
                {quantityLabel(row.quantity)}
              </TableCell>
              <TableCell className="tabular-nums">
                {row.cost !== null
                  ? poundsFromPence(row.cost)
                  : QUOTE_REQUEST_WORDING.cantQuote}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
        {page.despatch_by && (
          <>
            <dt className="text-muted-foreground">
              {QUOTE_REQUEST_WORDING.despatchBy}
            </dt>
            <dd>{weekdayDayMonth(page.despatch_by)}</dd>
          </>
        )}
        {page.valid_until && (
          <>
            <dt className="text-muted-foreground">Valid until</dt>
            <dd>{weekdayDayMonth(page.valid_until)}</dd>
          </>
        )}
        {page.your_reference && (
          <>
            <dt className="text-muted-foreground">Your reference</dt>
            <dd>{page.your_reference}</dd>
          </>
        )}
        {quotePdf && (
          <>
            <dt className="text-muted-foreground">
              {QUOTE_REQUEST_WORDING.yourQuote}
            </dt>
            <dd className="inline-flex flex-wrap items-center gap-1">
              <FileText className="size-4 text-muted-foreground" aria-hidden />
              <span>
                {quotePdf.filename}
                <span className="text-muted-foreground">
                  {" · "}
                  {fileSize(quotePdf.size_bytes)}
                </span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`${ACTION_WORDING.download} ${quotePdf.filename}`}
                onClick={() => onDownload(quotePdf)}
              >
                <Download aria-hidden />
                {ACTION_WORDING.download}
              </Button>
            </dd>
          </>
        )}
      </dl>
      {onChange && (
        <StaffViewReadOnly>
          <div>
            <Button type="button" variant="outline" onClick={onChange}>
              {QUOTE_REQUEST_WORDING.changePrices}
            </Button>
          </div>
        </StaffViewReadOnly>
      )}
    </Card>
  );
}
